<template>
  <div class="ps-component-wrapper">
    <transition name="fade">
      <div v-show="validationMsg" class="validation--popoup">
        <p>
          {{ validationMsg }}
        </p>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <form
        v-if="cart.item_count"
        class="site-cart"
        action="/cart"
        method="post"
        novalidate
        @submit="checkout">
        <div class="cart__header mta-center">
          <!-- Header left -->
          <div class="cart__header-left">
            <h1 class="cart__title">{{ $language.cart.title }}</h1>
          </div>
          <!-- Header right -->
          <div class="cart__header-right tablet-up">
            <span class="cart__total-items">
              You have <strong>{{ cart.item_count }} products</strong> in your
              cart.
            </span>
            <span
              class="cart__total-price dta-right"
              :class="{ 'disabled--text': isUpdating }">
              {{ $language.cart.subtotal }}:
              <strong>{{ $money(cart.total_price) }}</strong>
            </span>
            <input
              type="submit"
              name="checkout"
              value="> Checkout"
              :disabled="isUpdating" />
          </div>
        </div>
        <ul class="cart__ul">
          <!-- Free shipping -->
          <transition name="fade">
            <li v-if="free_shipping" class="cart__free-shipping">
              {{ $language.cart.free_shipping }}
            </li>
          </transition>
          <!-- Cart items -->
          <li class="cart__li cart-list-header xlarge-down--hide">
            <span class="cart__image">PHOTO</span>
            <span class="cart__details">DESCRIPTION</span>
            <span class="cart__quantity">QUANTITY</span>
            <span class="cart__price-single">PRICE PER 1</span>
            <span class="cart__price-total">TOTAL PRICE</span>
            <span class="cart__remove"></span>
          </li>
          <transition-group name="fade" mode="out-in">
            <li
              v-for="(item, idx) in cart.items"
              :key="item.key"
              class="cart__li">
              <a :href="item.url" class="cart__image">
                <img :src="item.image" class="lazyload" />
              </a>
              <div class="cart__details">
                <span class="cart__details-title">
                  {{ item.product_title }}
                </span>
                <span class="cart__details-variant">
                  {{ item.variant_title }}
                </span>
                <span
                  class="cart__details-short-description"
                  ref="shortdescription"
                  :class="getLineShortDescription(item.handle, idx)">
                </span>
              </div>
              <div class="cart__quantity">
                <p class="cart__item__label">QUANTITY</p>
                <div
                  class="item__quantity"
                  v-if="item.product_type == 'Stair Nosing'">
                  <template v-if="item.properties && item.properties['Qty']">
                    <span class="quantity__stair-nosing">{{
                      item.properties['Qty']
                    }}</span>
                  </template>
                </div>
                <div
                  class="item__quantity"
                  v-else-if="item.product_type == 'KitBox'">
                  <span class="quantity__stair-nosing">{{
                    item.variant_title
                  }}</span>
                </div>
                <div class="cart__quantity-toggle" v-else>
                  <button
                    class="item__quantity-minus"
                    @click="updateQuantity(item.key, item.quantity - 1)"
                    :disabled="isUpdating">
                    <IconMinus />
                  </button>
                  <input
                    class="item__quantity-input"
                    name="updates[]"
                    type="text"
                    :value="item.quantity"
                    pattern="[0-9]*"
                    min="0"
                    aria-label="quantity"
                    @change.prevent="
                      updateQuantity(item.key, Number($event.target.value))
                    "
                    :disabled="isUpdating" />
                  <button
                    class="item__quantity-plus"
                    @click="updateQuantity(item.key, item.quantity + 1)"
                    :disabled="isUpdating">
                    <IconPlus />
                  </button>
                </div>
                <span
                  class="cart__details-unit"
                  ref="unit"
                  :class="getLineUnit(item.handle, idx)">
                </span>
                <span>
                  {{ item.properties.unit }}
                </span>
              </div>
              <div class="cart__price-single">
                <p class="cart__item__label">PRICE PER 1</p>
                <span
                  class="cart__details-price"
                  :class="{ 'disabled--text': isUpdating }">
                  {{ $money(item.original_price) }}
                </span>
              </div>
              <div class="cart__price-total">
                <p class="cart__item__label">TOTAL PRICE</p>
                <span
                  class="cart__details-price"
                  :class="{ 'disabled--text': isUpdating }">
                  {{ $money(item.line_price) }}
                </span>
              </div>
              <div class="cart__remove">
                <button
                  class="cart__remove"
                  @click.prevent="updateQuantity(item.key, 0)">
                  {{ $language.cart.remove }}
                </button>
              </div>
            </li>
          </transition-group>
        </ul>
        <div class="cart__actions">
          <!-- Note -->
          <div class="cart__note">
            <label class="cart__note-btn" for="cart-note">{{
              cart.note ? 'EDIT NOTE' : 'ORDER NOTES'
            }}</label>
            <textarea
              v-model="cart.note"
              @keyup="updateNote()"
              class="cart__note-textarea"
              name="note"
              id="cart-note"
              rows="3"
              :placeholder="$language.cart.note"></textarea>
          </div>
          <!-- Checkout -->
          <span
            class="cart__total-price mobile-down mta-center"
            :class="{ 'disabled--text': isUpdating }">
            {{ $language.cart.subtotal }} {{ $money(cart.total_price) }}
          </span>
          <div class="cart__actions-btns">
            <input
              type="submit"
              name="checkout"
              class="btn btn--primary btn--blackwash cart__checkout"
              :value="$language.cart.checkout"
              :disabled="isUpdating" />
            <a href="/collections/all" class="btn btn--whitewash">
              {{ $language.cart.continue_browsing }}
            </a>
          </div>
        </div>
      </form>
      <div v-else-if="!mini && hasBuiltCart" class="cart__empty">
        <p>{{ $language.cart.empty }}</p>
        <br />
        <a href="/collections/all">Continue Shopping</a>
      </div>
    </transition>
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce';
import { updateItem } from '../../scripts/cart';
import IconMinus from '../icons/IconMinus';
import IconPlus from '../icons/IconPlus';

export default {
  components: { IconMinus, IconPlus },

  props: ['mini', 'show_free_shipping', 'free_shipping_threshold'],

  computed: {
    free_shipping() {
      return (
        this.show_free_shipping == 'true' &&
        this.number(this.cart.total_price) >
          parseInt(this.free_shipping_threshold)
      );
    },
  },

  data() {
    return {
      cart: {},
      number_format: '{{amount}}',
      isUpdating: true,
      hasBuiltCart: false,
      validationMsg: null,
    };
  },

  methods: {
    buildCart() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((data) => {
          this.cart = { ...data };
          this.isUpdating = false;
          this.hasBuiltCart = true;
        });
    },

    async checkout(event) {
      this.stairnoseChecker(event);
      this.bundleChecker(event);
    },

    // Validate against a minimum order quantity
    stairnoseChecker(event) {
      let totalLinealMeters = 0;
      for (const item of this.cart.items) {
        if (
          item.product_type === 'Stair Nosing' &&
          item.properties &&
          item.properties._total_lm
        ) {
          totalLinealMeters += Number(item.properties._total_lm);
        }
      }
      if (totalLinealMeters > 0 && totalLinealMeters < 2700) {
        this.validationMsg = 'Minimum order quantity of 2,700mm';
        setTimeout(() => {
          this.validationMsg = null;
        }, 3000);
        event.preventDefault();
      }
    },

    // Create a draft order checkout if a bundle is present
    async bundleChecker(event) {
      for (const item of this.cart.items) {
        if (item.properties && item.properties._bundle_product == 1) {
          event.preventDefault();

          const createDraftOrder = await fetch('/tools/ps/draft_order', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(this.cart),
          });

          const response = await createDraftOrder.json();
          if (response.message === 'success') {
            window.location = response.data.checkout_url;
          } else {
            alert('Oops, please refresh your cart and try again.');
          }

          break;
        }
      }
    },

    updateQuantity(key, qty) {
      this.isUpdating = true;
      updateItem(key, { quantity: qty }).then((state) => {
        this.cart = { ...state };
        this.isUpdating = false;
      });
    },

    getLineShortDescription(handle, idx) {
      fetch(`/products/${handle}?view=metafields`)
        .then((res) => res.json())
        .then((data) => {
          this.$refs.shortdescription[idx].innerText =
            data.short_description.value;
        });
    },

    getLineUnit(handle, idx) {
      fetch(`/products/${handle}?view=metafields`)
        .then((res) => res.json())
        .then((data) => {
          this.$refs.unit[idx].innerText = data.unit.value;
        });
    },

    updateNote: debounce(function () {
      fetch('/cart/update.js', {
        method: 'post',
        body: new URLSearchParams(`note=${this.cart.note}`),
      });
    }, 1000),

    number(amount) {
      return formatMoney(amount, this.number_format);
    },

    popInOut() {
      this.$actions.toggleMiniCart();
      setTimeout(() => {
        if (this.$store.miniCartActive) {
          this.$actions.toggleMiniCart();
        }
      }, 1500);
    },
  },

  watch: {
    cart() {
      // Update the cart count bubble
      document.querySelector('.cart-item-count').innerText =
        this.cart.item_count;
    },
  },

  mounted() {
    this.buildCart();
  },
};
</script>
